/**
 * Record Result Modal - Enter match scores
 */
import { useState } from 'react';

export default function RecordResultModal({ fixture, onSubmit, onClose }) {
  const [form, setForm] = useState({
    team_a_score: '',
    team_a_wickets: '',
    team_a_overs: '',
    team_b_score: '',
    team_b_wickets: '',
    team_b_overs: '',
    result_type: 'normal',
    winner_team_id: '',
    result_summary: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!form.team_a_score || !form.team_b_score) {
      return;
    }

    if (form.result_type === 'normal' && !form.winner_team_id) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        team_a_score: parseInt(form.team_a_score),
        team_a_wickets: parseInt(form.team_a_wickets) || 0,
        team_a_overs: parseFloat(form.team_a_overs) || 0,
        team_b_score: parseInt(form.team_b_score),
        team_b_wickets: parseInt(form.team_b_wickets) || 0,
        team_b_overs: parseFloat(form.team_b_overs) || 0,
        result_type: form.result_type,
        winner_team_id: form.result_type === 'tie' || form.result_type === 'no_result' ? null : form.winner_team_id,
        result_summary: form.result_summary || generateResultSummary(),
      });
    } finally {
      setLoading(false);
    }
  };

  const generateResultSummary = () => {
    if (form.result_type === 'tie') return 'Match Tied';
    if (form.result_type === 'no_result') return 'No Result';
    if (form.result_type === 'abandoned') return 'Match Abandoned';
    
    const winner = form.winner_team_id === fixture.team_a_id ? fixture.team_a_name : fixture.team_b_name;
    const scoreA = parseInt(form.team_a_score);
    const scoreB = parseInt(form.team_b_score);
    
    if (form.winner_team_id === fixture.team_a_id) {
      // Team A won
      const wicketsLost = parseInt(form.team_a_wickets) || 0;
      const wicketsRemaining = 10 - wicketsLost;
      return `${winner} won by ${wicketsRemaining} wicket${wicketsRemaining !== 1 ? 's' : ''}`;
    } else {
      // Team B won
      const runsDiff = scoreB - scoreA;
      return `${winner} won by ${runsDiff} run${runsDiff !== 1 ? 's' : ''}`;
    }
  };

  const RESULT_TYPES = [
    { value: 'normal', label: 'Normal Result' },
    { value: 'tie', label: 'Tie' },
    { value: 'super_over', label: 'Super Over' },
    { value: 'no_result', label: 'No Result' },
    { value: 'walkover', label: 'Walkover' },
    { value: 'abandoned', label: 'Abandoned' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="record-result-modal-backdrop">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="record-result-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-lg text-[#2B3A67]">Record Match Result</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="ph-bold ph-x text-xl"></i>
          </button>
        </div>

        {/* Match Info */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-700">{fixture.team_a_name || 'Team A'}</span>
            <span className="text-xs text-gray-400">vs</span>
            <span className="font-medium text-gray-700">{fixture.team_b_name || 'Team B'}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Team A Score */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              {fixture.team_a_name || 'Team A'} Score
            </label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Runs</label>
                <input
                  type="number"
                  min={0}
                  value={form.team_a_score}
                  onChange={e => setForm(prev => ({ ...prev, team_a_score: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-center font-mono focus:border-[#3AAFA9] focus:outline-none"
                  placeholder="0"
                  required
                  data-testid="team-a-runs"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Wickets</label>
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={form.team_a_wickets}
                  onChange={e => setForm(prev => ({ ...prev, team_a_wickets: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-center font-mono focus:border-[#3AAFA9] focus:outline-none"
                  placeholder="0"
                  data-testid="team-a-wickets"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Overs</label>
                <input
                  type="number"
                  step="0.1"
                  min={0}
                  value={form.team_a_overs}
                  onChange={e => setForm(prev => ({ ...prev, team_a_overs: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-center font-mono focus:border-[#3AAFA9] focus:outline-none"
                  placeholder="0.0"
                  data-testid="team-a-overs"
                />
              </div>
            </div>
          </div>

          {/* Team B Score */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              {fixture.team_b_name || 'Team B'} Score
            </label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Runs</label>
                <input
                  type="number"
                  min={0}
                  value={form.team_b_score}
                  onChange={e => setForm(prev => ({ ...prev, team_b_score: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-center font-mono focus:border-[#3AAFA9] focus:outline-none"
                  placeholder="0"
                  required
                  data-testid="team-b-runs"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Wickets</label>
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={form.team_b_wickets}
                  onChange={e => setForm(prev => ({ ...prev, team_b_wickets: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-center font-mono focus:border-[#3AAFA9] focus:outline-none"
                  placeholder="0"
                  data-testid="team-b-wickets"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Overs</label>
                <input
                  type="number"
                  step="0.1"
                  min={0}
                  value={form.team_b_overs}
                  onChange={e => setForm(prev => ({ ...prev, team_b_overs: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-center font-mono focus:border-[#3AAFA9] focus:outline-none"
                  placeholder="0.0"
                  data-testid="team-b-overs"
                />
              </div>
            </div>
          </div>

          {/* Result Type */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Result Type</label>
            <div className="flex flex-wrap gap-2">
              {RESULT_TYPES.map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, result_type: type.value }))}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    form.result_type === type.value
                      ? 'bg-[#3AAFA9] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Winner Selection */}
          {!['tie', 'no_result', 'abandoned'].includes(form.result_type) && (
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Winner</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, winner_team_id: fixture.team_a_id }))}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    form.winner_team_id === fixture.team_a_id
                      ? 'border-[#3AAFA9] bg-[#3AAFA9]/10'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="winner-team-a"
                >
                  <div className="font-medium text-sm">{fixture.team_a_name || 'Team A'}</div>
                  {form.winner_team_id === fixture.team_a_id && (
                    <i className="ph-fill ph-check-circle text-[#3AAFA9] mt-1"></i>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, winner_team_id: fixture.team_b_id }))}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    form.winner_team_id === fixture.team_b_id
                      ? 'border-[#3AAFA9] bg-[#3AAFA9]/10'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="winner-team-b"
                >
                  <div className="font-medium text-sm">{fixture.team_b_name || 'Team B'}</div>
                  {form.winner_team_id === fixture.team_b_id && (
                    <i className="ph-fill ph-check-circle text-[#3AAFA9] mt-1"></i>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Result Summary */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              Result Summary <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={form.result_summary}
              onChange={e => setForm(prev => ({ ...prev, result_summary: e.target.value }))}
              placeholder="e.g., Team A won by 5 wickets"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:border-[#3AAFA9] focus:outline-none"
              data-testid="result-summary"
            />
            <p className="text-xs text-gray-400 mt-1">Leave empty to auto-generate</p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !form.team_a_score || !form.team_b_score || 
                (!['tie', 'no_result', 'abandoned'].includes(form.result_type) && !form.winner_team_id)}
              className="flex-1 py-2.5 bg-[#3AAFA9] text-white font-bold rounded-xl hover:bg-[#2D8A85] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="submit-result-btn"
            >
              {loading ? (
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
              ) : (
                <>
                  <i className="ph-bold ph-check"></i>
                  Save Result
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
